/**
 * What is on screen after everything is gone.
 *
 * FRONTEND_DESIGN §4.10: "After deletion, show a blank first-run state or a
 * content-free receipt—not prior learner details." This is the receipt, and
 * "content-free" is the constraint that shapes every line of it: the receipt
 * says which categories were removed and which tables were emptied, by name,
 * and nothing about what was in them. Not a row, not a value, not a digest of
 * a value, not a display name.
 *
 * It cannot accidentally say more, because it renders the server's response
 * and the server's response is the contract's shape — which has no field a row
 * could travel in. What this file is responsible for is not adding one: no
 * prop from anywhere else in the app, no cached query, no "you were studying
 * X" line reconstructed from state that outlived the deletion.
 *
 * There is nothing to retry and no link away. The server stopped after
 * answering — it had to, since the deletion closed the database every other
 * route reads through — so a Retry button would fail, and a navigation would
 * land on a screen that cannot load. Saying so plainly is the honest end.
 */

import type { DeleteAllDataResponse } from '@davinci/contracts';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    heading: 'Everything was deleted',
    writtenStatus:
      'DaVinci has stopped. This page is the only record of what happened that is still on your screen \u2014 a copy of it was written to a file beside the folder that was removed.',
    notWrittenStatus:
      'DaVinci has stopped. Everything below was removed, and it is gone whatever else this screen says.',
    notSavedLead: 'DaVinci could not save a copy of this receipt.',
    notSavedBody:
      'The deletion itself finished \u2014 the folder is gone and the tables listed below were emptied \u2014 but the file that would let you check this afterwards was not written.',
    terminalReason:
      'DaVinci printed the reason in the terminal it was started from, just before it stopped. It is worth reading: most causes are ordinary \u2014 a full disk, a permission change \u2014 but one of them means another program on this machine interfered with where the file was going, and that one does not fix itself.',
    onlyRecord:
      'This page is now the only record. Nothing here can be recovered by reloading, because the deletion closed the database and DaVinci has stopped.',
    deletedAt: (at: string, appVersion: string, schemaVersion: string | number) =>
      `Deleted at ${at} by DaVinci ${appVersion}, under database schema ${schemaVersion}.`,
    removed: 'Removed',
    heldFiles: '(held files)',
    wasEmpty: '(was empty)',
    emptied: 'Tables that held something, now empty',
    recreated: 'Recreated, empty',
    notReachable: 'Not reachable by walking your records',
    notReachableHint:
      'These were removed with the folder. They are named because DaVinci could not have reached them by walking your records alone, and a completeness claim that hid that would be worth less than the gap it hid.',
    notReached: 'What this deletion did not reach',
    firstRun: 'Starting DaVinci again gives you a genuine first run. Nothing from before survives it.',
    database: 'The database',
    blobs: 'Stored files',
    imports: 'Imported source material',
    backups: 'Backups DaVinci made',
    logs: 'Local logs',
    deletionReceipts: 'Records of previous deletions',
    unexpected: 'Left alone: something DaVinci did not put there',
  },
  he: {
    heading: 'הכול נמחק',
    writtenStatus:
      'DaVinci נעצר. העמוד הזה הוא התיעוד היחיד של מה שקרה שעדיין מופיע על המסך שלך \u2014 עותק שלו נכתב לקובץ ליד התיקייה שהוסרה.',
    notWrittenStatus: 'DaVinci נעצר. כל מה שמופיע למטה הוסר, והוא נעלם, בלי קשר למה שכתוב במסך הזה.',
    notSavedLead: 'DaVinci לא הצליח לשמור עותק של הקבלה הזו.',
    notSavedBody:
      'המחיקה עצמה הסתיימה \u2014 התיקייה נעלמה והטבלאות שברשימה למטה רוקנו \u2014 אבל הקובץ שהיה מאפשר לבדוק את זה אחר כך לא נכתב.',
    terminalReason:
      'DaVinci הדפיס את הסיבה בטרמינל שממנו הופעל, רגע לפני שנעצר. כדאי לקרוא אותה: רוב הסיבות רגילות \u2014 דיסק מלא, שינוי הרשאות \u2014 אבל אחת מהן אומרת שתוכנה אחרת במכשיר הזה התערבה במקום שאליו הקובץ היה אמור להגיע, וזו לא מסתדרת מעצמה.',
    onlyRecord:
      'העמוד הזה הוא עכשיו התיעוד היחיד. אי אפשר לשחזר כאן דבר בטעינה מחדש, כי המחיקה סגרה את מסד הנתונים ו-DaVinci נעצר.',
    deletedAt: (at: string, appVersion: string, schemaVersion: string | number) =>
      `נמחק בתאריך ${at} על ידי DaVinci ${appVersion}, לפי סכמת מסד נתונים ${schemaVersion}.`,
    removed: 'הוסרו',
    heldFiles: '(החזיק קבצים)',
    wasEmpty: '(היה ריק)',
    emptied: 'טבלאות שהחזיקו משהו, וכעת הן ריקות',
    recreated: 'נוצרו מחדש, ריקות',
    notReachable: 'לא ניתנים להגעה בסריקת הרשומות שלך',
    notReachableHint:
      'אלה הוסרו יחד עם התיקייה. הם מוזכרים כי DaVinci לא יכול היה להגיע אליהם בסריקת הרשומות שלך בלבד, וטענת שלמות שהסתירה זאת הייתה שווה פחות מהפער שהיא הסתירה.',
    notReached: 'מה שהמחיקה הזו לא הגיעה אליו',
    firstRun: 'הפעלה מחדש של DaVinci תיתן לך הפעלה ראשונה אמיתית. שום דבר מלפני כן לא שורד אותה.',
    database: 'מסד הנתונים',
    blobs: 'קבצים שמורים',
    imports: 'חומר מקור שיובא',
    backups: 'גיבויים ש-DaVinci יצר',
    logs: 'יומנים מקומיים',
    deletionReceipts: 'תיעוד של מחיקות קודמות',
    unexpected: 'נשאר כפי שהיה: משהו ש-DaVinci לא הניח שם',
  },
});

type ReceiptMessages = (typeof messages)['en'];

const CATEGORY_KEYS: Readonly<Record<string, keyof ReceiptMessages>> = {
  database: 'database',
  blobs: 'blobs',
  imports: 'imports',
  backups: 'backups',
  logs: 'logs',
  'deletion-receipts': 'deletionReceipts',
  unexpected: 'unexpected',
};


export interface DeletionReceiptProps {
  readonly receipt: DeleteAllDataResponse;
}

export function DeletionReceipt({ receipt }: DeletionReceiptProps): React.JSX.Element {
  const t = useMessages(messages);
  return (
    <section className="panel" aria-labelledby="receipt-heading">
      <h2 id="receipt-heading">{t.heading}</h2>
      {/*
       * The announcement is a paragraph, not the heading. `role` on an `h2`
       * replaces the heading role rather than adding to it, so a screen reader
       * would gain a status message and lose the structure the rest of this
       * page is navigated by. `status` rather than `alert` because the founder
       * asked for this and it succeeded — an assertive interruption would
       * announce a completed intention as if something had gone wrong.
       */}
      {/*
       * Two outcomes, not one, and the difference is issue #24. The deletion
       * happens before the receipt is written, so a full disk or a permission
       * change in between leaves a deletion that succeeded with no record of it
       * on disk. This screen used to state the copy as a fact, which was the
       * frontend half of the same wrong answer the server was giving: the
       * founder was told a file existed that did not.
       *
       * Both branches say the same thing first — it is gone — because that is
       * the part that is true in both, and burying it under a warning would
       * make a missing receipt look like a failed deletion. Which is the exact
       * confusion this fixes.
       */}
      {receipt.receiptWritten ? (
        <p role="status">{t.writtenStatus}</p>
      ) : (
        <p role="status">{t.notWrittenStatus}</p>
      )}

      {!receipt.receiptWritten ? (
        <div className="notice notice-error" role="alert">
          <p>
            <strong>{t.notSavedLead}</strong> {t.notSavedBody}
          </p>
          {/*
           * Deliberately not a list of likely causes. The first version of
           * this paragraph offered three — a full disk, a permission change,
           * something already sitting in the way — and a boundary review
           * pointed out what that does to the fourth: DaVinci also declines to
           * write when the path stops resolving outside the data root between
           * its two checks, which is a thing another program on this machine
           * did, not an accident. Naming only the innocent causes would tell
           * the founder to go and free some disk space.
           *
           * So it says where the answer is instead of guessing at it. The
           * server prints the reason before it stops, and that line
           * distinguishes the two — which is the whole reason it is logged.
           */}
          <p>{t.terminalReason}</p>
          <p>{t.onlyRecord}</p>
        </div>
      ) : null}
      <p>{t.deletedAt(receipt.deletedAt, receipt.appVersion, receipt.schemaVersion)}</p>

      <h3>{t.removed}</h3>
      <ul className="plain-list">
        {receipt.categories.map((category) => (
          <li key={`${category.kind}-${category.path}`}>
            <strong>{categoryLabel(t, category.kind)}</strong> —{' '}
            <code>{category.path}</code> {category.heldFiles ? t.heldFiles : t.wasEmpty}
          </li>
        ))}
      </ul>

      {receipt.tablesEmptied.length > 0 ? (
        <>
          <h3>{t.emptied}</h3>
          <ul className="plain-list">
            {receipt.tablesEmptied.map((table) => (
              <li key={table}>
                <code>{table}</code>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{t.recreated}</h3>
      <ul className="plain-list">
        {receipt.tablesReinitialised.map((table) => (
          <li key={table}>
            <code>{table}</code>
          </li>
        ))}
      </ul>

      {receipt.notReachableByLearnerTraversal.length > 0 ? (
        <>
          <h3>{t.notReachable}</h3>
          <p className="field-hint">{t.notReachableHint}</p>
          <ul className="plain-list">
            {receipt.notReachableByLearnerTraversal.map((row) => (
              <li key={row.table}>
                <code>{row.table}</code> — {row.reason}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h3>{t.notReached}</h3>
      <ul className="plain-list">
        {receipt.outsideOurControl.map((limit) => (
          <li key={limit}>{limit}</li>
        ))}
      </ul>

      <p>{t.firstRun}</p>
    </section>
  );
}

function categoryLabel(t: ReceiptMessages, kind: string): string {
  const key = CATEGORY_KEYS[kind];
  return key === undefined ? kind : (t[key] as string);
}
